import { FC, useEffect } from "react";
import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { createRoot } from "react-dom/client";
import { handleConsentChangeForAmplitude } from "~/utils/amplitude/config";
import { AppProvider } from "~/utils/AppProvider";
import { App } from "./App";

import "~/utils/i18next";
handleConsentChangeForAmplitude();

const { VITE_ENVIRONMENT, VITE_LD_CLIENT_SIDE_ID } = import.meta.env;

Sentry.init({
  attachStacktrace: true,
  dsn: "https://8bd7e5720104422681a430579736b53e@o1325523.ingest.sentry.io/6584786",
  enabled: VITE_ENVIRONMENT !== "development",
  environment: VITE_ENVIRONMENT,
  integrations: [
    Sentry.replayIntegration(),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.2,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
});

const BaseAppComponent: FC = () => (
  <AppProvider>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </AppProvider>
);

const BaseApp = Sentry.withProfiler(BaseAppComponent);

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: VITE_LD_CLIENT_SIDE_ID,
    options: {
      bootstrap: "localStorage",
    },
  });

  const container = document.getElementById("root");
  const root = createRoot(container!);

  root.render(
    <LDProvider>
      <BaseApp />
    </LDProvider>,
  );
})();
